import React, { useState, useEffect } from 'react';
import { ShieldCheck, Clock, AlertTriangle } from 'lucide-react';
import VerifyIdentityButton from '../Identity/VerifyIdentityButton';
import { readUserFromStorage } from '../../utils/subscription';
import {
  IDENTITY_FAILED,
  IDENTITY_PENDING,
  IDENTITY_VERIFIED,
  getIdentityStatus,
  fetchIdentityProfile,
} from '../../utils/identityVerification';
import '../Identity/Identity.css';
import '../../App.css';

/**
 * Required for Owner-Operator and Fleet/Pro before finishing onboarding.
 * Onboarding.js skips this step entirely for Free tier.
 */
const VerifyIdentityStep = ({ onContinue, onBack }) => {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { user: fresh, status: next } = await fetchIdentityProfile();
        if (cancelled) return;
        setUser(fresh);
        setStatus(next);
      } catch {
        if (cancelled) return;
        // Fall back to the cached user so a network blip can't trap onboarding.
        const cached = readUserFromStorage();
        setUser(cached);
        setStatus(getIdentityStatus(cached));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="onboarding-content">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Checking your verification status...</p>
        </div>
      </div>
    );
  }

  const isVerified = status === IDENTITY_VERIFIED;
  const isPending = status === IDENTITY_PENDING;
  const hasFailed = status === IDENTITY_FAILED;

  return (
    <div className="onboarding-content" id="sovrynn-onboarding-verify-step">
      <h2 className="step-title">Verify your identity</h2>
      <p className="step-description">
        {hasFailed
          ? "Verification didn't go through last time. Let's give it another try — brokers rely on this to trust your Compliance Passport™."
          : 'Confirm who you are with a photo ID. This keeps your account and your Compliance Passport™ trusted by brokers, and takes about two minutes.'}
      </p>

      {isVerified && (
        <div className="identity-status-panel">
          <span className="identity-status-icon is-success">
            <ShieldCheck size={28} />
          </span>
          <p>Your identity is verified. You&apos;re all set.</p>
        </div>
      )}

      {isPending && (
        <div className="identity-status-panel">
          <span className="identity-status-icon is-progress">
            <Clock size={28} />
          </span>
          <p>Verification in progress — this can take a few minutes.</p>
          <p className="identity-status-note">
            You can continue now; we&apos;ll update your status automatically.
          </p>
        </div>
      )}

      {!isVerified && !isPending && (
        <div className="identity-step-actions">
          {hasFailed && (
            <span className="identity-status-icon is-warning">
              <AlertTriangle size={28} />
            </span>
          )}
          <VerifyIdentityButton user={user} status={status} />
          {/* Navigation only — status stays as-is and the dashboard keeps prompting. */}
          <button type="button" className="identity-skip-link" onClick={onContinue}>
            Skip for now
          </button>
        </div>
      )}

      <div className="onboarding-buttons">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        {(isVerified || isPending) && (
          <button type="button" className="btn btn-primary" onClick={onContinue}>
            Continue →
          </button>
        )}
      </div>
    </div>
  );
};

export default VerifyIdentityStep;
